// src\components\VideoEngine\Common\TransitionWrapper.js

//This component handles the "Slash," "Zoom," and "Vertical" transitions. 
// // The most important logic here is the clip-path polygon math for the slice effect.

import React from "react";
import { AbsoluteFill, useCurrentFrame, interpolate, Easing } from "remotion";

export const TransitionWrapper = ({ 
    children, 
    isFirst, 
    overlap = 30, // Default overlap
    type = "wipe", 
    theme 
}) => {
  const frame = useCurrentFrame();

  // If it's the first slide, no transition needed.
  if (isFirst) return <>{children}</>;

  // SHARED: The 0 -> 1 progress driver (Bezier curve for "Snap" feel)
  const progress = interpolate(frame, [0, overlap], [0, 1], {
    extrapolateRight: "clamp",
    easing: Easing.bezier(0.22, 1, 0.36, 1),
  });

  // ✅ SAFETY CHECK: If transition is done, remove clip-path entirely
  // This guarantees no lingering artifacts.
  if (progress === 1) {
    return <AbsoluteFill>{children}</AbsoluteFill>;
  }

  // --------------------------------------------------------
  // OPTION 1: THE VERTICAL SNAP
  // --------------------------------------------------------
  if (type === "vertical") {
    const translateY = interpolate(progress, [0, 1], [1080, 0]);
    return (
      <AbsoluteFill style={{ transform: `translateY(${translateY}px)` }}>
        {children}
      </AbsoluteFill>
    );
  }

  // --------------------------------------------------------
  // OPTION 2: THE KINETIC SLICE (The "Slash")
  // --------------------------------------------------------
  if (type === "slice") {
    const tilt = 20; // degree of tilt
    
    // We multiply progress by 140 (not 100) to ensure the tilted top-edge clears the screen.
    const p = progress * 140;

    // Polygon math: Top-Right, Bottom-Right, Bottom-Left, Top-Left
    // We animate the "Left" edge moving from Right to Left
    const clipPath = `polygon(
      ${100 - p + tilt}% 0%, 
      100% 0%, 
      100% 100%, 
      ${100 - p - tilt}% 100%
    )`;

    return (
      <AbsoluteFill style={{ clipPath }}>
        {children}
        {/* The Glowing "Cut" Line */}
        <div
            style={{
                position: 'absolute',
                // Match the lead edge of the polygon
                left: `${100 - p}%`, 
                top: -100, // Extend up to cover tilt gap
                bottom: -100, // Extend down to cover tilt gap
                width: '6px', // Slightly thicker for impact
                background: 'white',
                transform: `skewX(-${tilt}deg)`, 
                boxShadow: `0 0 20px ${theme.primary}`,
                opacity: 1 - progress 
            }} 
        />
      </AbsoluteFill>
    );
  }

  // --------------------------------------------------------
  // OPTION 3: THE DEEP DIVE (Zoom)
  // --------------------------------------------------------
  if (type === "zoom") {
    const scale = interpolate(progress, [0, 1], [1.3, 1]);
    const opacity = interpolate(progress, [0, 0.6], [0, 1]);

    return (
      <AbsoluteFill style={{ transform: `scale(${scale})`, opacity }}>
        {children}
      </AbsoluteFill>
    );
  }

  // --------------------------------------------------------
  // FALLBACK: CLASSIC WIPE
  // --------------------------------------------------------
  const defaultClip = `inset(0px ${interpolate(progress, [0, 1], [100, 0])}% 0px 0px)`;
  return (
    <AbsoluteFill style={{ clipPath: defaultClip }}>
      {children}
      <div
        style={{
          position: "absolute",
          right: 0,
          top: 0,
          bottom: 0,
          width: 5,
          background: "white",
          boxShadow: "0 0 40px white",
          opacity: 1 - progress,
        }}
      />
    </AbsoluteFill>
  );
};